/**
 * similar-products.js
 *
 * WHAT THIS DOES:
 * 1. Reads UTM parameters from the URL (set by Google Ads)
 * 2. Fetches similar products via Shopify Storefront API using tag query
 *
 * GOOGLE ADS SETUP:
 * Campaign URL suffix (Shopping campaign → Settings → Campaign URL options):
 *   utm_source=google&utm_medium=shopping&utm_brand={BRAND}&utm_category={CATEGORY}&utm_tons={TONS}
 *
 * Replace {BRAND} with your literal brand slug per Shopping campaign:
 *   goodman | daikin | mitsubishi | mrcool | ge | amana | wrangler
 */

(function () {
  'use strict';

  // ─────────────────────────────────────────────
  // 1. INIT
  // ─────────────────────────────────────────────

  const root = document.getElementById('similar-products');
  if (!root) return;

  const SHOP = root.dataset.shopDomain;
  const TOKEN = root.dataset.storefrontToken;
  const productSku = root.dataset.productSku || '';
  const params = new URLSearchParams(window.location.search);
  const defaultBrnad = root.dataset.productVendor || '';

  // Read UTM params set by Google Ads campaign URL suffix
  const brand = (params.get('utm_brand') || '').toLowerCase();
  const category = (params.get('utm_category') || '').toLowerCase();
  const tons = (params.get('utm_tons') || '').toLowerCase();
  const campaign = (params.get('utm_campaign') || '').toLowerCase();


  // ─────────────────────────────────────────────
  // 2. STOREFRONT API — related products
  // ─────────────────────────────────────────────

  const STOREFRONT_QUERY = `
    query DynamicRelated($q: String!, $first: Int!) {
      products(query: $q, sortKey: BEST_SELLING, first: $first) {
        edges {
          node {
            id handle title
            priceRange {
              minVariantPrice { amount currencyCode }
              maxVariantPrice { amount currencyCode }
            }
            featuredImage { url altText }
            metafield(namespace: "custom", key: "seer2_rating") { value }
            tags
          }
        }
      }
    }
  `;

  async function fetchRelated() {
    if (!TOKEN || !SHOP) return [];

    // Build query from UTM params
    const tagParts = [];

    // Always enforce the brand explicitly via the vendor filter, regardless of what the tag arrays say.
    // If no brand is in the URL, fallback to goodman.
    tagParts.push(`vendor:${brand || defaultBrnad}`);

    if (category) tagParts.push(`tag:${category}`);
    if (tons) tagParts.push(`tag:${tons}`);

    // If we have a SKU, add a match for the first 5 characters
    if (productSku && productSku.length >= 4) {
      const prefix = productSku.substring(0, 4);
      tagParts.push(`(sku:${prefix}* OR title:${prefix}*)`);
    }

    // Skip sold out products
    tagParts.push('available_for_sale:true');

    // Join with AND
    const query = tagParts.join(' AND ');

    try {
      const res = await fetch(`https://${SHOP}/api/2024-01/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': TOKEN
        },
        body: JSON.stringify({
          query: STOREFRONT_QUERY,
          variables: { q: query, first: 10 }
        })
      });
      const { data } = await res.json();
      return data?.products?.edges?.map(e => e.node.handle) || [];
    } catch (e) {
      console.warn('[ShoppingLanding] Storefront API fetch failed:', e);
      return [];
    }
  }

  async function renderRelatedProducts(handles) {
    const grid = document.getElementById('similar-products-grid');
    if (!grid) return;

    if (!handles.length) {
      grid.innerHTML = '<p class="sl-no-results">No related products found.</p>';
      return;
    }

    grid.innerHTML = ''; // Clear skeletons

    const htmlPromises = handles.map(async (handle) => {
      try {
        const res = await fetch(`/products/${handle}?section_id=ajax-product-card`);
        const html = await res.text();
        return html;
      } catch (e) {
        console.error('Failed to load related product card for: ', handle);
        return '';
      }
    });

    const cardsHtml = await Promise.all(htmlPromises);
    grid.innerHTML = cardsHtml.join('');
  }

  // After full load: fire API fetch for related products
  window.addEventListener('load', async () => {
    const products = await fetchRelated();
    renderRelatedProducts(products);
  });

})();